import { useEffect, useState } from "react";
import { api, STATUS_COLORS } from "../api";
import { useLang, fmtDate } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function Leave() {
  const { lang } = useLang();
  const bm = lang === "bm";
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leave_type: "", start_date: "", end_date: "", days: 1, reason: "" });

  const load = () => {
    api.get("/leave/balances").then((r) => setBalances(r.data)).catch(() => {});
    api.get("/leave/requests", { params: { scope: "mine" } }).then((r) => setRequests(r.data)).catch(() => {});
    api.get("/leave/types").then((r) => setTypes(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/leave/requests", form);
      toast.success(bm ? "Permohonan cuti dihantar" : "Leave request submitted");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="leave-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Cuti" : "Leave"}</h1>
        <Button data-testid="leave-apply-button" className="bg-blue-800 hover:bg-blue-900" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> {bm ? "Mohon Cuti" : "Apply Leave"}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {balances.map((b) => (
          <Card key={b.balance_id} data-testid={`leave-balance-${b.leave_type.replace(/\s/g, "-")}`}>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400 truncate">{b.leave_type}</p>
              <p className="mt-1 text-2xl font-extrabold font-mono">{Math.max(0, b.entitled - b.used)}</p>
              <p className="text-xs text-slate-500">{bm ? `daripada ${b.entitled} hari` : `of ${b.entitled} days`}</p>
              <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-blue-700 rounded-full" style={{ width: `${Math.min(100, (b.used / b.entitled) * 100)}%` }} />
              </div>
            </CardContent>
          </Card>
        ))}
        {balances.length === 0 && <p className="text-sm text-slate-500 col-span-full">{bm ? "Tiada rekod baki cuti (akaun pentadbir)." : "No leave balance records (admin account)."}</p>}
      </div>

      <div className="bg-card border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{bm ? "Jenis" : "Type"}</TableHead>
              <TableHead>{bm ? "Tarikh" : "Dates"}</TableHead>
              <TableHead>{bm ? "Hari" : "Days"}</TableHead>
              <TableHead className="hidden md:table-cell">{bm ? "Pelulus" : "Approver"}</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.request_id} data-testid={`leave-request-${r.request_id}`}>
                <TableCell className="text-sm font-medium">{r.leave_type}</TableCell>
                <TableCell className="text-sm">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</TableCell>
                <TableCell className="font-mono text-sm">{r.days}</TableCell>
                <TableCell className="hidden md:table-cell text-sm">{r.approver_name}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[r.status] || ""}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
            {requests.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-slate-500 py-8">{bm ? "Tiada permohonan cuti lagi." : "No leave requests yet."}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="leave-apply-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{bm ? "Mohon Cuti" : "Apply Leave"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">{bm ? "Jenis Cuti" : "Leave Type"}</Label>
              <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                <SelectTrigger data-testid="leave-type-select"><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  {types.map((t) => <SelectItem key={t.type_id} value={t.name}>{bm ? t.name_bm : t.name} ({t.entitled}d)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{bm ? "Mula" : "Start"}</Label>
                <Input data-testid="leave-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{bm ? "Tamat" : "End"}</Label>
                <Input data-testid="leave-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{bm ? "Bilangan Hari" : "Days"}</Label>
              <Input data-testid="leave-days" type="number" min="0.5" step="0.5" value={form.days} onChange={(e) => setForm({ ...form, days: parseFloat(e.target.value) })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{bm ? "Sebab" : "Reason"}</Label>
              <Textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
            <Button data-testid="leave-apply-submit-button" className="w-full bg-blue-800 hover:bg-blue-900" onClick={submit}>
              {bm ? "Hantar Permohonan" : "Submit Request"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
